/**
 * CollectionPage Component
 * Collection/category listing page with Shopify API-side filters
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'motion/react';
import { Layout } from '~/components/layout/Layout';
import { ProductGrid } from '~/components/product/ProductGrid';
import { getCollectionByHandle } from '~/lib/shopify/api';
import { useServerData } from '~/lib/server-data';
import type { Product, Collection, PageInfo, SortKey } from '~/lib/shopify/types';
import {
  generateCollectionPageStructuredData,
} from '~/lib/seo/structured-data';
import { generateCollectionMetaTags } from '~/lib/seo/meta-tags';
import { trackViewItemList } from '~/lib/tracking/gtm';

interface CollectionPageProps {
  handle: string;
}

interface FilterValue {
  id: string;
  label: string;
  count: number;
  input: string;
}

interface AvailableFilter {
  id: string;
  label: string;
  type: string;
  values: FilterValue[];
}

const sortOptions: Array<{ label: string; value: SortKey; reverse: boolean }> = [
  { label: 'Best Selling', value: 'BEST_SELLING', reverse: false },
  { label: 'Newest', value: 'CREATED', reverse: true },
  { label: 'Price: Low to High', value: 'PRICE', reverse: false },
  { label: 'Price: High to Low', value: 'PRICE', reverse: true },
  { label: 'Alphabetical', value: 'TITLE', reverse: false },
];

// Handle aliases: map user-friendly URL slugs to Shopify collection handles
const HANDLE_ALIASES: Record<string, string> = {
  'poly': 'polycom',
  'wireless': 'wireless-headsets',
};

// Filter IDs we want to show (Shopify returns many, we pick the useful ones)
// These match Shopify's filter.id values
const DISPLAY_FILTER_ORDER = [
  'filter.v.availability',
  'filter.p.vendor',
  'filter.v.price',
  'filter.p.product_type',
  'filter.p.tag',
];

// Friendly labels for filter sections
const FILTER_LABELS: Record<string, string> = {
  'filter.v.availability': 'Availability',
  'filter.p.vendor': 'Brand',
  'filter.v.price': 'Price',
  'filter.p.product_type': 'Type',
  'filter.p.tag': 'Features',
};

export function CollectionPage({ handle: rawHandle }: CollectionPageProps) {
  const handle = HANDLE_ALIASES[rawHandle] || rawHandle;
  const serverData = useServerData();
  const serverCollection = serverData.collectionData?.[handle];
  const [collection, setCollection] = useState<Collection | null>(serverCollection?.collection || null);
  const [products, setProducts] = useState<Product[]>(serverCollection?.products || []);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(serverCollection?.pageInfo || null);
  const [isLoading, setIsLoading] = useState(!serverCollection);
  const [sortIndex, setSortIndex] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const usedServerData = useRef(!!serverCollection);

  // Selected filter inputs: maps filter ID → Set of selected value input strings
  const [selectedInputs, setSelectedInputs] = useState<Record<string, Set<string>>>({});
  const [availableFilters, setAvailableFilters] = useState<AvailableFilter[]>([]);

  const currentSort = sortOptions[sortIndex];

  // Build Shopify filters array from selected inputs
  const buildFiltersArray = useCallback(() => {
    const filters: Record<string, unknown>[] = [];
    for (const inputSet of Object.values(selectedInputs)) {
      for (const input of inputSet) {
        try {
          filters.push(JSON.parse(input));
        } catch {
          // skip malformed inputs
        }
      }
    }
    return filters.length > 0 ? filters : undefined;
  }, [selectedInputs]);

  const activeFilterCount = Object.values(selectedInputs).reduce((sum, set) => sum + set.size, 0);

  // Fetch collection data (reacts to handle, sort, or filter changes)
  useEffect(() => {
    const isInitialWithServerData = usedServerData.current && sortIndex === 0 && activeFilterCount === 0;

    async function fetchCollection() {
      // For initial load with server data, fetch in background just for filters
      if (!isInitialWithServerData) {
        setIsLoading(true);
      }

      try {
        const filters = buildFiltersArray();
        const data = await getCollectionByHandle({
          handle,
          first: 48,
          sortKey: currentSort.value,
          reverse: currentSort.reverse,
          filters,
        });

        // Always update filters from API response
        setAvailableFilters(data.availableFilters);

        if (isInitialWithServerData) {
          // Server data already has products/collection — only needed filters
          usedServerData.current = false;
        } else {
          setCollection(data.collection);
          setProducts(data.products);
          setPageInfo(data.pageInfo);
          if (data.collection) {
            trackViewItemList(data.products, data.collection.title);
          }
        }
      } catch (error) {
        console.error('Failed to fetch collection:', error);
      } finally {
        if (!isInitialWithServerData) {
          setIsLoading(false);
        }
      }
    }

    fetchCollection();
  }, [handle, currentSort, activeFilterCount]);

  // Update SEO
  useEffect(() => {
    if (!collection) return;

    const meta = generateCollectionMetaTags(collection);
    document.title = meta.title;

    let descMeta = document.querySelector('meta[name="description"]');
    if (!descMeta) {
      descMeta = document.createElement('meta');
      descMeta.setAttribute('name', 'description');
      document.head.appendChild(descMeta);
    }
    descMeta.setAttribute('content', meta.description);

    const breadcrumbs = [
      { name: 'Home', url: '/' },
      { name: collection.title, url: `/collections/${collection.handle}` },
    ];
    const structuredData = generateCollectionPageStructuredData(collection, products, breadcrumbs);
    const scriptId = 'collection-structured-data';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, [collection, products]);

  const handleLoadMore = async () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;

    setIsLoading(true);
    try {
      const filters = buildFiltersArray();
      const data = await getCollectionByHandle({
        handle,
        first: 48,
        after: pageInfo.endCursor,
        sortKey: currentSort.value,
        reverse: currentSort.reverse,
        filters,
      });

      setProducts((prev) => [...prev, ...data.products]);
      setPageInfo(data.pageInfo);
    } catch (error) {
      console.error('Failed to load more products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFilterValue = (filterId: string, input: string) => {
    setSelectedInputs((prev) => {
      const next = { ...prev };
      const current = new Set(prev[filterId] || []);
      if (current.has(input)) {
        current.delete(input);
      } else {
        current.add(input);
      }
      if (current.size === 0) {
        delete next[filterId];
      } else {
        next[filterId] = current;
      }
      return next;
    });
  };

  const clearFilters = () => {
    setSelectedInputs({});
  };

  // Filter out empty sections and order them
  const displayFilters = DISPLAY_FILTER_ORDER
    .map((id) => availableFilters.find((f) => f.id === id))
    .filter((f): f is AvailableFilter => !!f && f.values.length > 0);

  return (
    <Layout>
      <div className="container-wide py-8 lg:py-12">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <a href="/" className="text-accent-silver hover:text-accent-white transition-colors">
                Home
              </a>
            </li>
            <li className="text-accent-silver">/</li>
            <li className="text-accent-white">{collection?.title || 'Collection'}</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="heading-md text-accent-white mb-4"
          >
            {collection?.title || 'Products'}
          </motion.h1>
          {collection?.description && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-accent-silver max-w-3xl"
            >
              {collection.description}
            </motion.p>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-6 border-b border-surface-slate">
          <div className="flex items-center gap-4">
            {/* Filter Toggle */}
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`btn-secondary py-2.5 ${filtersOpen ? 'bg-surface-ash' : ''}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 w-5 h-5 bg-brand-orange text-white text-xs rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Active Filter Pills */}
            {activeFilterCount > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {Object.entries(selectedInputs).map(([filterId, inputSet]) => {
                  const filter = availableFilters.find((f) => f.id === filterId);
                  return Array.from(inputSet).map((input) => {
                    const value = filter?.values.find((v) => v.input === input);
                    return (
                      <button
                        key={`${filterId}-${input}`}
                        onClick={() => toggleFilterValue(filterId, input)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-xs rounded-full hover:bg-brand-orange/20 transition-colors"
                      >
                        {value?.label || 'Filter'}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    );
                  });
                })}
                <button
                  onClick={clearFilters}
                  className="text-xs text-accent-silver hover:text-accent-white transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Results Count */}
            <span className="text-sm text-accent-silver">
              {products.length} {products.length === 1 ? 'product' : 'products'}
            </span>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-accent-silver">Sort by:</span>
            <select
              value={sortIndex}
              onChange={(e) => setSortIndex(Number(e.target.value))}
              className="input py-2 pr-10 w-auto"
            >
              {sortOptions.map((option, i) => (
                <option key={`${option.value}-${option.reverse}`} value={i}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filters Panel */}
        {filtersOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8 pb-6 border-b border-surface-slate"
          >
            {displayFilters.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {displayFilters.map((filter) => (
                  <FilterSection
                    key={filter.id}
                    filter={filter}
                    selectedInputs={selectedInputs[filter.id] || new Set()}
                    onToggle={(input) => toggleFilterValue(filter.id, input)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-accent-silver">Loading filters...</p>
            )}
          </motion.div>
        )}

        {/* Products Grid */}
        <ProductGrid products={products} isLoading={isLoading && products.length === 0} listName={collection?.title} />

        {/* No Results */}
        {!isLoading && products.length === 0 && activeFilterCount > 0 && (
          <div className="text-center py-16">
            <p className="text-accent-silver mb-4">No products match your filters.</p>
            <button onClick={clearFilters} className="btn-secondary">
              Clear Filters
            </button>
          </div>
        )}

        {/* Load More */}
        {pageInfo?.hasNextPage && (
          <div className="mt-12 text-center">
            <button
              onClick={handleLoadMore}
              disabled={isLoading}
              className="btn-secondary px-8 py-4"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading...
                </span>
              ) : (
                'Load More Products'
              )}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}

// ============================================================================
// Filter Section Component
// ============================================================================

function FilterSection({
  filter,
  selectedInputs,
  onToggle,
}: {
  filter: AvailableFilter;
  selectedInputs: Set<string>;
  onToggle: (input: string) => void;
}) {
  const label = FILTER_LABELS[filter.id] || filter.label;
  const isPriceRange = filter.type === 'PRICE_RANGE';

  // For price range filters, show predefined buckets
  if (isPriceRange) {
    return (
      <div>
        <h3 className="text-sm font-medium text-accent-white mb-3">{label}</h3>
        <div className="space-y-2">
          {filter.values.map((value) => {
            // Parse the price range input to create a readable label
            const priceLabel = formatPriceLabel(value);
            return (
              <label key={value.id} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedInputs.has(value.input)}
                  onChange={() => onToggle(value.input)}
                  className="w-4 h-4 rounded border-surface-slate bg-surface-coal text-brand-orange focus:ring-brand-orange focus:ring-offset-0"
                />
                <span className="text-sm text-accent-silver group-hover:text-accent-white transition-colors">
                  {priceLabel}
                </span>
                <span className="text-xs text-accent-silver/50 ml-auto">({value.count})</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  // Standard checkbox filter (brand, type, tag, availability)
  // Limit to values with count > 0
  const visibleValues = filter.values.filter((v) => v.count > 0);
  if (visibleValues.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-medium text-accent-white mb-3">{label}</h3>
      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
        {visibleValues.map((value) => (
          <label key={value.id} className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={selectedInputs.has(value.input)}
              onChange={() => onToggle(value.input)}
              className="w-4 h-4 rounded border-surface-slate bg-surface-coal text-brand-orange focus:ring-brand-orange focus:ring-offset-0"
            />
            <span className="text-sm text-accent-silver group-hover:text-accent-white transition-colors truncate">
              {value.label}
            </span>
            <span className="text-xs text-accent-silver/50 ml-auto flex-shrink-0">({value.count})</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function formatPriceLabel(value: FilterValue): string {
  try {
    const parsed = JSON.parse(value.input);
    const price = parsed.price;
    if (price) {
      const min = price.min != null ? Number(price.min) : null;
      const max = price.max != null ? Number(price.max) : null;
      if (min != null && max != null) return `£${min} – £${max}`;
      if (min != null) return `£${min}+`;
      if (max != null) return `Under £${max}`;
    }
  } catch {
    // fallback
  }
  return value.label;
}
